import React from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];

/**
 * @param {object} props
 * @param {{ count: number, page: number, page_size: number, total_pages: number }} props.meta
 * @param {(page: number) => void} props.onPageChange
 * @param {number} [props.pageSize]
 * @param {(size: number) => void} [props.onPageSizeChange]
 * @param {number[]} [props.pageSizeOptions]
 */
export default function DataTablePagination({
  meta,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  testId = "data-table-pagination",
  compact = false,
}) {
  const { count, page, page_size: metaPageSize, total_pages: totalPages } = meta;
  const effectivePageSize = pageSize ?? metaPageSize ?? 25;

  if (!count) return null;

  const start = (page - 1) * effectivePageSize + 1;
  const end = Math.min(page * effectivePageSize, count);
  const showPager = totalPages > 1;

  return (
    <div
      className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 min-w-0 ${
        compact ? "pt-2" : "pt-4 border-t"
      }`}
      data-testid={testId}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 min-w-0">
        <p className="text-[11px] sm:text-xs text-muted-foreground tabular">
          {start}–{end} of {count}
        </p>
        {onPageSizeChange && (
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Rows</span>
            <Select
              value={String(effectivePageSize)}
              onValueChange={(v) => onPageSizeChange(Number(v))}
            >
              <SelectTrigger className="h-8 w-[72px] text-xs" data-testid={`${testId}-page-size`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pageSizeOptions.map((n) => (
                  <SelectItem key={n} value={String(n)} data-testid={`${testId}-page-size-${n}`}>
                    {n}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
      {showPager && (
        <div className="flex items-center justify-between sm:justify-end gap-1.5 shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={compact ? "h-7 px-2 text-xs" : undefined}
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            data-testid={`${testId}-prev`}
          >
            <ChevronLeft className={`h-4 w-4 ${compact ? "" : "mr-1"}`} />
            {!compact && "Previous"}
          </Button>
          <span className="text-[11px] sm:text-xs tabular-nums px-1">
            {page}/{totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={compact ? "h-7 px-2 text-xs" : undefined}
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            data-testid={`${testId}-next`}
          >
            {!compact && "Next"}
            <ChevronRight className={`h-4 w-4 ${compact ? "" : "ml-1"}`} />
          </Button>
        </div>
      )}
    </div>
  );
}
